import { useCapabilities } from "src/hooks/useCapabilities";
import { flowFolderScope } from "src/hooks/useCapabilities/flowFolderScope";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { DashboardPageParams } from "src/router/urls";
import { useUserRoles } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

// These see the full UI through the staff capability mirror but hold only
// read grants on the data plane, so the job write is rejected — skip the
// call instead of collecting a 403 on every authoring page load.
const READ_ONLY_STAFF_ROLES = ["fwd_viewer", "oncall_group"];
// Internal roles that inherit d_admin and can write jobs; holding one for the
// current org wins over a read-only staff role on the same token. The fwd_*
// roles are org-scoped; the oncall/taktile ones are global.
const JOB_WRITE_STAFF_ROLES = [
  "fwd_d",
  "oncall_write",
  "taktile_admin",
  "taktile_d_admin",
];

/**
 * @returns whether the current user may create the test-run job backing the
 * Run flow button, so the call is skipped when the backend would reject it.
 */
export const useCanCreateTestRunJob = () => {
  const { jobs } = useCapabilities();
  const { flow } = useAuthoringContext();
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const roles = useUserRoles();

  const holdsForOrg = (names: string[]) =>
    roles?.some((role) => {
      if (!names.includes(role.name)) {
        return false;
      }
      const roleOrg = role.arguments.org_id;
      return roleOrg === undefined || roleOrg === "*" || roleOrg === orgId;
    }) ?? false;
  const isReadOnlyStaffSession =
    holdsForOrg(READ_ONLY_STAFF_ROLES) && !holdsForOrg(JOB_WRITE_STAFF_ROLES);

  return !isReadOnlyStaffSession && jobs.canCreate(flowFolderScope(flow));
};
